/**
 * Centralized production audio declarations shared by all three scenario packs.
 * Files are produced by `scripts/process-audio.mjs` from pinned CC0 archives.
 * Prefer Ogg Opus; AAC/M4A fallbackPath covers Safari/WebKit.
 * Keep this module browser-safe (no Node fs).
 */
import type { AssetManifestEntry } from "@desk/core";
import { OMINOUS_AUDIO_ASSET_ID, WARM_AUDIO_ASSET_ID } from "./ids.js";

export const PRODUCTION_MUSIC_ASSET_IDS = [
  WARM_AUDIO_ASSET_ID, // Airy bed (Clean UI treatment remains silence)
  "music-pulse",
  "music-sector",
] as const;

export const PRODUCTION_SFX_ASSET_IDS = [
  "sfx-whoosh",
  "sfx-impact",
  "sfx-notify-positive",
  "sfx-notify-alert",
  "sfx-glitch",
  "sfx-success",
  OMINOUS_AUDIO_ASSET_ID,
] as const;

function audio(
  id: string,
  path: string,
  fallbackPath: string,
  author: string,
  sourceUrl: string,
  tags: string[],
  modifications: string,
): AssetManifestEntry {
  return {
    id,
    kind: "audio",
    path,
    fallbackPath,
    license: "CC0 1.0",
    author,
    sourceUrl,
    downloadedAt: "2026-08-08",
    modifications,
    tags,
  };
}

const OGA_PAGE = "https://opengameart.org/content/dark-sci-fi-audio-pack";
const KENNEY_DIGITAL = "https://kenney.nl/assets/digital-audio";
const KENNEY_IMPACT = "https://kenney.nl/assets/impact-sounds";
const KENNEY_INTERFACE = "https://kenney.nl/assets/interface-sounds";

const MUSIC_MOD =
  "Trimmed to a 20s loop window, 50ms edge fades, loudnorm ~-18 LUFS / <=-1.5 dBTP, preferred Ogg Opus 48kbps stereo + AAC/M4A 48kbps stereo Safari fallback. See licenses/audio/provenance.json.";
const SFX_MOD =
  "Silence trimmed, peak-limited, preferred mono Ogg Opus 64kbps + AAC/M4A 64kbps Safari fallback (max 3s). See licenses/audio/provenance.json.";

/** Shared audio manifest entries across the shift's stories. */
export const PRODUCTION_AUDIO_ASSETS: AssetManifestEntry[] = [
  audio(WARM_AUDIO_ASSET_ID, "assets/audio/music-airy.ogg", "assets/audio/music-airy.m4a", "SRG774", OGA_PAGE, ["warm", "soft", "airy", "bed", "music"], MUSIC_MOD),
  audio("music-pulse", "assets/audio/music-pulse.ogg", "assets/audio/music-pulse.m4a", "SRG774", OGA_PAGE, ["pulse", "urgent", "bed", "music"], MUSIC_MOD),
  audio("music-sector", "assets/audio/music-sector.ogg", "assets/audio/music-sector.m4a", "SRG774", OGA_PAGE, ["tense", "sector", "bed", "music"], MUSIC_MOD),
  audio("sfx-whoosh", "assets/audio/sfx-whoosh.ogg", "assets/audio/sfx-whoosh.m4a", "Kenney", KENNEY_DIGITAL, ["whoosh", "motion", "sfx"], SFX_MOD),
  audio("sfx-impact", "assets/audio/sfx-impact.ogg", "assets/audio/sfx-impact.m4a", "Kenney", KENNEY_IMPACT, ["impact", "hit", "sfx"], SFX_MOD),
  audio(
    "sfx-notify-positive",
    "assets/audio/sfx-notify-positive.ogg",
    "assets/audio/sfx-notify-positive.m4a",
    "Kenney",
    KENNEY_INTERFACE,
    ["notify", "positive", "sfx"],
    SFX_MOD,
  ),
  audio(
    "sfx-notify-alert",
    "assets/audio/sfx-notify-alert.ogg",
    "assets/audio/sfx-notify-alert.m4a",
    "Kenney",
    KENNEY_INTERFACE,
    ["notify", "alert", "sfx"],
    SFX_MOD,
  ),
  audio("sfx-glitch", "assets/audio/sfx-glitch.ogg", "assets/audio/sfx-glitch.m4a", "Kenney", KENNEY_DIGITAL, ["glitch", "digital", "sfx"], SFX_MOD),
  audio("sfx-success", "assets/audio/sfx-success.ogg", "assets/audio/sfx-success.m4a", "Kenney", KENNEY_DIGITAL, ["success", "positive", "sfx"], SFX_MOD),
  audio(
    OMINOUS_AUDIO_ASSET_ID,
    "assets/audio/sfx-alarm-sting.ogg",
    "assets/audio/sfx-alarm-sting.m4a",
    "Kenney",
    KENNEY_INTERFACE,
    ["ominous", "alarm", "sting", "sfx"],
    SFX_MOD,
  ),
];

export function productionAudioAssetIds(): string[] {
  return PRODUCTION_AUDIO_ASSETS.map((a) => a.id);
}
